import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import UtilService from "../../../services/UtilService";
import { PAY_PLAN_APP, payPlanChipColor, resolvePayPlan } from "../../../services/PayPlanUtil";
import AccountService from "../AccountService";

const SECTIONS = [
    {
        key: "standard",
        title: "Tutor Standard",
        price: "$4.99 / month",
    },
    {
        key: "annual",
        title: "Tutor Annual",
        price: "$49.00 / year",
    },
    {
        key: "extra",
        title: "Extra 50 PDF Exports",
        price: "$4.99 one-time",
    },
];

function textOf(value) {
    if (value == null || value === "") return "";
    if (typeof value === "string" || typeof value === "number") return String(value);
    return "";
}

function subscriptionHaystack(sub) {
    const parts = [
        sub?.planId,
        sub?.priceKey,
        sub?.price_key,
        sub?.productKey,
        sub?.product_key,
        sub?.stripePriceId,
        sub?.priceId,
        sub?.productId,
        sub?.appCode,
        sub?.app,
    ];
    const meta = sub?.metadata;
    if (meta && typeof meta === "object") {
        parts.push(meta.planId, meta.plan_id, meta.priceKey, meta.price_key, meta.productKey, meta.product_key);
    }
    const lines = sub?.invoicePaymentSucceeded?.data?.object?.lines?.data;
    if (Array.isArray(lines)) {
        lines.forEach((line) => parts.push(line?.description, line?.price?.nickname));
    }
    return parts.map(textOf).filter(Boolean).join(" ").toLowerCase();
}

function tutorBucket(sub) {
    const text = subscriptionHaystack(sub);
    if (!text.includes("tutor") && !text.includes("extra_pdf") && !text.includes("extra pdf")) {
        return null;
    }
    if (text.includes("extra") || text.includes("pdf")) return "extra";
    if (text.includes("year") || text.includes("annual")) return "annual";
    if (text.includes("month")) return "standard";
    if (text.includes("tutor_standard") || text.includes("tutor standard")) return "standard";
    return null;
}

function detailNumber(details, ...keys) {
    for (const key of keys) {
        const value = Number(details?.[key]);
        if (Number.isFinite(value)) return value;
    }
    return 0;
}

function extraSessions(details) {
    const raw = details?.tutor_extra_pdf_sessions ?? details?.tutorExtraPdfSessions;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === "object") return Object.values(raw);
    return [];
}

function sessionLabel(item, index) {
    if (typeof item === "string") return item;
    if (item && typeof item === "object") {
        return textOf(item.sessionId || item.id || item.checkoutSessionId) || `Purchase ${index + 1}`;
    }
    return `Purchase ${index + 1}`;
}

function statusColor(status) {
    const value = String(status || "").toUpperCase();
    if (value === "ACTIVE" || value === "SUCCEEDED" || value === "PAID") return "success";
    if (value === "CANCELED" || value === "CANCELLED" || value === "INACTIVE") return "warning";
    return "default";
}

function SubscriptionRows({ rows }) {
    if (rows.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                No subscription on record.
            </Typography>
        );
    }
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell>Stripe subscription</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.id || `${row.planId}-${row.createdAt}`}>
                        <TableCell>
                            <Chip size="small" label={row.status || "—"} color={statusColor(row.status)} />
                        </TableCell>
                        <TableCell>{row.planId || "—"}</TableCell>
                        <TableCell>{UtilService.formatDate(row.startAt) || "—"}</TableCell>
                        <TableCell>{UtilService.formatDate(row.endAt) || "—"}</TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                            {row.stripeSubscriptionId || "—"}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

export default function TutorSubsAdmin({ userId, userDetails, onGranted }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [granting, setGranting] = useState(false);
    const [grantMessage, setGrantMessage] = useState("");
    const [creditAction, setCreditAction] = useState(null);
    const testAccount = Boolean(userDetails?.testAccount);
    const tutorPlan = resolvePayPlan(userDetails, PAY_PLAN_APP.TUTOR);
    const details = userDetails?.details || {};
    const extraCredits = detailNumber(details, "extra_pdf_credits", "extraPdfCredits");
    const pdfExports = details.tutor_pdf_exports || details.tutorPdfExports || {};
    const pdfUsed = detailNumber(pdfExports, "used");
    const sessions = extraSessions(details);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!userId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const data = await AccountService.getUserSubscriptions(userId, 0, 100, [
                    { field: "createdAt", sort: "desc" },
                ]);
                if (!cancelled) {
                    setRows(Array.isArray(data?.content) ? data.content : []);
                    setError("");
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Could not load tutor subscriptions.");
                    setRows([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [userId]);

    const grouped = useMemo(() => {
        const next = { standard: [], annual: [], extra: [] };
        rows.forEach((row) => {
            const bucket = tutorBucket(row);
            if (bucket) next[bucket].push(row);
        });
        return next;
    }, [rows]);

    const handleGrantTestCredit = async () => {
        if (!userId || !testAccount || granting || tutorPlan === "STANDARD") return;
        setGranting(true);
        setGrantMessage("");
        setError("");
        try {
            await AccountService.grantTutorTestCredit(userId);
            if (typeof onGranted === "function") {
                await onGranted();
            }
            setGrantMessage("Added Tutor Standard and 50 PDF credits.");
            setCreditAction(null);
        } catch (err) {
            setError(err?.message || "Could not add test credit.");
        } finally {
            setGranting(false);
        }
    };

    const handleRemoveTestCredit = async () => {
        if (!userId || !testAccount || granting) return;
        setGranting(true);
        setGrantMessage("");
        setError("");
        try {
            await AccountService.removeTutorTestCredit(userId);
            if (typeof onGranted === "function") {
                await onGranted();
            }
            setGrantMessage("Removed 50 PDF credits.");
            setCreditAction(null);
        } catch (err) {
            setError(err?.message || "Could not remove test credit.");
        } finally {
            setGranting(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Typography variant="h5">Tutor</Typography>
                <Chip
                    size="small"
                    label={`Tutor: ${tutorPlan}`}
                    color={payPlanChipColor(tutorPlan)}
                    variant="outlined"
                />
            </Box>
            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
            {grantMessage ? <Alert severity="success" sx={{ mb: 2 }}>{grantMessage}</Alert> : null}
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            ) : (
                SECTIONS.map((section) => {
                    const matches = grouped[section.key] || [];
                    const rowActive = matches.some((row) => String(row.status || "").toUpperCase() === "ACTIVE");
                    const annualActive = grouped.annual.some((row) => String(row.status || "").toUpperCase() === "ACTIVE");
                    const active = section.key === "extra"
                        ? rowActive || extraCredits > 0 || sessions.length > 0
                        : section.key === "annual"
                            ? rowActive
                            : rowActive || (tutorPlan === "STANDARD" && !annualActive);
                    return (
                        <Card key={section.key} sx={{ mb: 2 }}>
                            <CardContent>
                                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", justifyContent: "space-between" }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1.5 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {section.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {section.price}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={active ? "Active" : "Not active"}
                                                color={active ? "success" : "default"}
                                            />
                                        </Box>
                                        <SubscriptionRows rows={matches} />
                                    </Box>
                                    {section.key === "standard" ? (
                                        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexShrink: 0 }}>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setCreditAction("add")}
                                                disabled={!testAccount || granting || tutorPlan === "STANDARD" || active}
                                                sx={{ minHeight: 44, textTransform: "none" }}
                                            >
                                                Add credit
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={() => setCreditAction("remove")}
                                                disabled={!testAccount || granting}
                                                sx={{ minHeight: 44, textTransform: "none" }}
                                            >
                                                Remove credit
                                            </Button>
                                            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 220, textAlign: "right" }}>
                                                The button is available for testing accounts only.
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                                {section.key === "extra" ? (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="body2">
                                            Extra PDF credits left: {extraCredits}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            PDF exports used this period: {pdfUsed}
                                            {pdfExports.period ? ` (${pdfExports.period})` : ""}
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            Extra purchases: {sessions.length === 0 ? "None" : sessions.map(sessionLabel).join(", ")}
                                        </Typography>
                                    </Box>
                                ) : null}
                            </CardContent>
                        </Card>
                    );
                })
            )}
            <Dialog
                open={creditAction != null}
                onClose={() => {
                    if (!granting) {
                        setCreditAction(null);
                    }
                }}
                fullWidth
                maxWidth="xs"
            >
                <DialogContent>
                    <Typography sx={{ lineHeight: 1.7 }}>
                        This is testing purpose only.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setCreditAction(null)}
                        disabled={granting}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color={creditAction === "remove" ? "error" : "primary"}
                        onClick={creditAction === "remove" ? handleRemoveTestCredit : handleGrantTestCredit}
                        disabled={granting || !testAccount || (creditAction !== "remove" && tutorPlan === "STANDARD")}
                        sx={{ minHeight: 44, textTransform: "none" }}
                    >
                        {granting
                            ? (creditAction === "remove" ? "Removing..." : "Adding...")
                            : (creditAction === "remove" ? "Remove credit" : "Add credit")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
